import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gift, TicketPercent } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";

function fmtDiscount(c) {
  return c.discount_type === "percentage"
    ? `Diskon ${c.discount_value}%`
    : `Diskon Rp${Number(c.discount_value).toLocaleString("id-ID")}`;
}

export default function CouponClaim({ slug, satisfaction, name, whatsapp }) {
  const [coupons, setCoupons] = useState(null);
  const [claiming, setClaiming] = useState("");
  const [claimed, setClaimed] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/coupons/${slug}`, { params: { satisfaction } });
      setCoupons(data);
    } catch {
      setCoupons([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, satisfaction]);

  const claim = async (c) => {
    setClaiming(c.id);
    try {
      const { data } = await api.post("/public/vouchers/claim", {
        outlet_slug: slug, coupon_id: c.id, satisfaction, name, whatsapp,
      });
      setClaimed(data);
      toast.success("Voucher berhasil diambil");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengambil voucher");
      await load();
    } finally { setClaiming(""); }
  };

  if (claimed) {
    const v = claimed.voucher, c = claimed.coupon;
    return (
      <div className="bg-white border-2 border-emerald-200 rounded-2xl p-5 text-left space-y-3 mt-2" data-testid="voucher-card">
        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 text-center">Voucher Anda</div>
        <div className="text-center space-y-1">
          <div className="text-xl font-extrabold" data-testid="voucher-coupon-name">{c.name}</div>
          <div className="text-sm text-emerald-700 font-semibold">{fmtDiscount(c)}</div>
          {c.min_transaction > 0 && <div className="text-xs text-zinc-500">Min. transaksi Rp{Number(c.min_transaction).toLocaleString("id-ID")}</div>}
          <div className="text-xs text-zinc-500">Berlaku sampai {dayjs(v.expires_at).format("DD MMMM YYYY")}</div>
        </div>
        <div className="bg-zinc-50 rounded-xl p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Kode</div>
          <div className="font-mono text-2xl font-bold tracking-widest" data-testid="voucher-code">{v.unique_code}</div>
        </div>
        <div className="flex justify-center" data-testid="voucher-qr"><QRCodeCanvas value={v.unique_code} size={140} /></div>
        <div className="text-center"><Badge className="bg-emerald-100 text-emerald-800 border-0" data-testid="voucher-status">AKTIF</Badge></div>
        <p className="text-xs text-center text-zinc-500">Tunjukkan kode atau QR ini kepada kasir saat digunakan.</p>
      </div>
    );
  }

  if (!coupons || coupons.length === 0) return null;

  return (
    <div className="text-left space-y-3 mt-2" data-testid="coupon-claim-section">
      <div className="flex items-center gap-2 justify-center text-sm font-semibold text-zinc-800">
        <Gift size={16} className="text-emerald-600" /> Ada voucher untuk Anda
      </div>
      {coupons.map(c => (
        <div key={c.id} className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid={`coupon-card-${c.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold">{c.name}</div>
              <div className="text-sm text-emerald-700 font-medium">{fmtDiscount(c)}</div>
              {c.min_transaction > 0 && <div className="text-xs text-zinc-500">Min. transaksi Rp{Number(c.min_transaction).toLocaleString("id-ID")}</div>}
              <div className="text-xs text-zinc-500">Berlaku sampai {dayjs(c.expiry_date).format("DD MMM YYYY")}</div>
              <div className="text-xs text-zinc-400">Sisa kuota: {c.remaining}</div>
            </div>
            <TicketPercent className="text-emerald-200 shrink-0" size={28} />
          </div>
          <Button onClick={() => claim(c)} disabled={!!claiming} className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 tap-target"
            data-testid={`claim-button-${c.id}`}>
            {claiming === c.id ? "Memproses..." : "Ambil Voucher"}
          </Button>
        </div>
      ))}
    </div>
  );
}
